import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AdminHeader from '../../components/admin/AdminHeader';
import authService from '../../services/authService';
import api from '../../services/api';

const MemberManagementPage = () => {
  const navigate = useNavigate();
  const { orgId } = useParams();
  const [adminUser, setAdminUser] = useState(null);
  const [activeTab, setActiveTab] = useState('members');
  const [loading, setLoading] = useState(true);
  const [organization, setOrganization] = useState(null);
  const [members, setMembers] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!authService.isAdminLoggedIn()) {
      navigate('/admin/login');
      return;
    }
    
    const userData = authService.getAdminUser();
    if (!userData) {
      navigate('/admin/login');
      return;
    }
    
    setAdminUser(userData);
    loadData();
  }, [navigate, orgId]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Load organization details
      const orgResponse = await api.getOrganization(orgId);
      setOrganization(orgResponse.data);
      
      // Load members
      const membersResponse = await api.getOrganizationMembers(orgId);
      setMembers(membersResponse.data.members || []);
      
    } catch (err) {
      console.error('Error loading member data:', err);
      setError('Failed to load member data');
    } finally {
      setLoading(false);
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'members':
        return <MembersTab members={members} organization={organization} onRefresh={loadData} />;
      case 'roles':
        return <RolesTab organization={organization} />;
      case 'audit':
        return <AuditTab organization={organization} />;
      default:
        return <MembersTab members={members} organization={organization} onRefresh={loadData} />;
    }
  };

  if (loading) {
    return (
      <div>
        <AdminHeader adminUser={adminUser} />
        <div style={{ padding: '20px' }}>Loading...</div>
      </div>
    );
  }

  return (
    <div>
      <AdminHeader adminUser={adminUser} />
      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '20px' }}>
        {/* Breadcrumb */}
        <div style={{ marginBottom: '16px', fontSize: '14px', color: '#6b7280' }}>
          <span 
            onClick={() => navigate('/admin/organizations')}
            style={{ cursor: 'pointer', color: '#2563eb', textDecoration: 'none' }}
          >
            Organizations
          </span>
          {' > '}
          <span style={{ color: '#111827' }}>
            {organization?.name || 'Organization'} Members
          </span>
        </div>

        {/* Header */}
        <div style={{ marginBottom: '24px' }}>
          <h2 style={{ margin: '0 0 8px 0', fontSize: '28px', color: '#111827' }}>
            Member Management
          </h2>
          <p style={{ margin: '0', fontSize: '14px', color: '#6b7280' }}>
            Manage members for {organization?.name || 'organization'}
          </p>
        </div>

        {error && (
          <div style={{
            padding: '12px',
            marginBottom: '20px',
            backgroundColor: '#fee',
            border: '1px solid #fcc',
            borderRadius: '6px',
            color: '#c00'
          }}>
            {error}
          </div>
        )}

        {/* Tabs */}
        <div style={{
          borderBottom: '1px solid #e5e7eb',
          marginBottom: '24px'
        }}>
          <div style={{ display: 'flex', gap: '32px' }}>
            <button
              onClick={() => setActiveTab('members')}
              style={{
                padding: '12px 0',
                border: 'none',
                background: 'none',
                fontSize: '14px',
                fontWeight: activeTab === 'members' ? '600' : '400',
                color: activeTab === 'members' ? '#2563eb' : '#6b7280',
                borderBottom: activeTab === 'members' ? '2px solid #2563eb' : '2px solid transparent',
                cursor: 'pointer'
              }}
            >
              Members
            </button>
            <button
              onClick={() => setActiveTab('roles')}
              style={{
                padding: '12px 0',
                border: 'none',
                background: 'none',
                fontSize: '14px',
                fontWeight: activeTab === 'roles' ? '600' : '400',
                color: activeTab === 'roles' ? '#2563eb' : '#6b7280',
                borderBottom: activeTab === 'roles' ? '2px solid #2563eb' : '2px solid transparent',
                cursor: 'pointer'
              }}
            >
              Roles & Permissions
            </button>
            <button
              onClick={() => setActiveTab('audit')}
              style={{
                padding: '12px 0',
                border: 'none',
                background: 'none',
                fontSize: '14px',
                fontWeight: activeTab === 'audit' ? '600' : '400',
                color: activeTab === 'audit' ? '#2563eb' : '#6b7280',
                borderBottom: activeTab === 'audit' ? '2px solid #2563eb' : '2px solid transparent',
                cursor: 'pointer'
              }}
            >
              Audit Log
            </button>
          </div>
        </div>

        {/* Tab Content */}
        {renderTabContent()}
      </div>
    </div>
  );
};

// Members Tab Component
const MembersTab = ({ members, organization, onRefresh }) => {
  const [showAddMember, setShowAddMember] = useState(false);
  const [newMember, setNewMember] = useState({ name: '', email: '', role: 'member' });
  const [processing, setProcessing] = useState(false);

  const roleLabels = {
    owner: 'Owner',
    level4: 'Manager',
    admin: 'Member Admin',
    member: 'Member'
  };

  const handleAddMember = async (e) => {
    e.preventDefault();
    try {
      setProcessing(true);
      // TODO: Implement member creation API call
      // await api.createMember(organization._id, newMember);
      console.log('Adding member:', newMember);
      alert('Member creation API not yet implemented');
      setShowAddMember(false);
      setNewMember({ name: '', email: '', role: 'member' });
      onRefresh();
    } catch (err) {
      console.error('Error adding member:', err);
      alert('Failed to add member');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <div>
          <h3 style={{ margin: '0', fontSize: '18px', color: '#111827' }}>
            Organization Members ({members.length})
          </h3>
          <p style={{ margin: '4px 0 0', fontSize: '14px', color: '#6b7280' }}>
            Manage member access and roles
          </p>
        </div>
        <button
          onClick={() => setShowAddMember(!showAddMember)}
          style={{
            padding: '10px 20px',
            backgroundColor: '#2563eb',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            fontSize: '14px',
            fontWeight: '500',
            cursor: 'pointer'
          }}
        >
          {showAddMember ? 'Cancel' : '+ Add Member'}
        </button>
      </div>

      {/* Add Member Form */}
      {showAddMember && (
        <div style={{
          padding: '20px',
          backgroundColor: '#f9fafb',
          border: '1px solid #e5e7eb',
          borderRadius: '8px',
          marginBottom: '20px'
        }}>
          <h4 style={{ margin: '0 0 16px', fontSize: '16px', color: '#111827' }}>Add New Member</h4>
          <form onSubmit={handleAddMember}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr 1fr', gap: '12px', marginBottom: '16px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', color: '#374151' }}>
                  Name *
                </label>
                <input
                  type="text"
                  value={newMember.name}
                  onChange={(e) => setNewMember({ ...newMember, name: e.target.value })}
                  required
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                  placeholder="John Doe"
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', color: '#374151' }}>
                  Email *
                </label>
                <input
                  type="email"
                  value={newMember.email}
                  onChange={(e) => setNewMember({ ...newMember, email: e.target.value })}
                  required
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                  placeholder="john@example.com"
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', color: '#374151' }}>
                  Role *
                </label>
                <select
                  value={newMember.role}
                  onChange={(e) => setNewMember({ ...newMember, role: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                >
                  <option value="member">Member</option>
                  <option value="admin">Member Admin</option>
                  <option value="level4">Manager</option>
                </select>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                type="submit"
                disabled={processing}
                style={{
                  padding: '8px 16px',
                  backgroundColor: processing ? '#9ca3af' : '#059669',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  cursor: processing ? 'not-allowed' : 'pointer'
                }}
              >
                {processing ? 'Adding...' : 'Add Member'}
              </button>
              <button
                type="button"
                onClick={() => setShowAddMember(false)}
                style={{
                  padding: '8px 16px',
                  backgroundColor: 'white',
                  color: '#374151',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Members Table */}
      <div style={{
        backgroundColor: 'white',
        border: '1px solid #e5e7eb',
        borderRadius: '8px',
        overflow: 'hidden'
      }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
              <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Name</th>
              <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Email</th>
              <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Role</th>
              <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Status</th>
              <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {members.length === 0 ? (
              <tr>
                <td colSpan="5" style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>
                  No members found. Add your first member above.
                </td>
              </tr>
            ) : (
              members.map((member, index) => (
                <tr key={member._id} style={{ borderBottom: index < members.length - 1 ? '1px solid #e5e7eb' : 'none' }}>
                  <td style={{ padding: '12px 16px', fontSize: '14px', color: '#111827' }}>
                    {member.name || 'Unknown'}
                  </td>
                  <td style={{ padding: '12px 16px', fontSize: '14px', color: '#6b7280' }}>
                    {member.user?.email || 'N/A'}
                  </td>
                  <td style={{ padding: '12px 16px', fontSize: '14px', color: '#6b7280' }}>
                    {roleLabels[member.role] || member.role}
                  </td>
                  <td style={{ padding: '12px 16px', fontSize: '14px' }}>
                    <span style={{
                      padding: '4px 8px',
                      borderRadius: '4px',
                      fontSize: '12px',
                      fontWeight: '500',
                      backgroundColor: member.status === 'active' ? '#d1fae5' : '#fee2e2',
                      color: member.status === 'active' ? '#065f46' : '#991b1b'
                    }}>
                      {member.status}
                    </span>
                  </td>
                  <td style={{ padding: '12px 16px', fontSize: '14px' }}>
                    <button
                      style={{
                        padding: '4px 12px',
                        fontSize: '13px',
                        color: '#2563eb',
                        backgroundColor: 'transparent',
                        border: '1px solid #2563eb',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        marginRight: '8px'
                      }}
                    >
                      Edit
                    </button>
                    {member.role !== 'owner' && (
                      <button
                        style={{
                          padding: '4px 12px',
                          fontSize: '13px',
                          color: '#dc2626',
                          backgroundColor: 'transparent',
                          border: '1px solid #dc2626',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                      >
                        Remove
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Roles Tab Component (placeholder)
const RolesTab = ({ organization }) => {
  return (
    <div style={{ padding: '20px', backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
      <h3 style={{ margin: '0 0 12px', fontSize: '18px', color: '#111827' }}>Roles & Permissions</h3>
      <p style={{ margin: '0', fontSize: '14px', color: '#6b7280' }}>
        Configure custom roles and permissions for organization members. (Coming soon)
      </p>
    </div>
  );
};

// Audit Tab Component (placeholder)
const AuditTab = ({ organization }) => {
  return (
    <div style={{ padding: '20px', backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
      <h3 style={{ margin: '0 0 12px', fontSize: '18px', color: '#111827' }}>Audit Log</h3>
      <p style={{ margin: '0', fontSize: '14px', color: '#6b7280' }}>
        View member activity history including additions, removals, and role changes. (Coming soon)
      </p>
    </div>
  );
};

export default MemberManagementPage;
